<template>
  <div
    ref="root"
    class="ssm-logo"
    :class="{ 'is-ready': ready }"
    @pointerenter="hovering = true"
    @pointerleave="hovering = false"
    @click="burst(900)"
  >
    <canvas
      ref="canvas"
      :width="size.width"
      :height="size.height"
      aria-hidden="true"
    />
  </div>
</template>

<script>
import { glitchLogo } from '@/pixel/glitch.js'
import { logoSize, renderGlitchedLetter, renderLogo } from '@/pixel/logo.js'
import { cloneBitmap, toCanvas } from '@/pixel/raster.js'
import { XorShift128 } from '@/pixel/rng.js'
import { reducedMotion, watchOnScreen, watchReducedMotion, whenIdle } from '@/motion.js'

// The poster logo, rendered from the same masks as the print. Every so often
// a letter twitches; every few seconds the letters themselves break: torn,
// mirrored, melting, turned into $, swapped around. More while hovered, a
// lot when clicked.

// Damaged versions pre-rendered per letter while the browser is idle.
const VARIANTS = 6

function toLayer (l) {
    return { canvas: toCanvas(l.image), x: l.x, y: l.y }
}

export default {
    props: { animated: { type: Boolean, default: true } },
    data () {
        return {
            size: logoSize(2),
            ready: false,
            hovering: false,
            onScreen: false,
            reduced: reducedMotion(),
        }
    },
    computed: {
        animating () {
            return this.animated && !this.reduced && this.onScreen
        },
    },
    watch: {
        animating () {
            this.scheduleIdle()
        },
        hovering () {
            this.scheduleIdle()
        },
    },
    mounted () {
        this.rng = new XorShift128((Math.random() * 2 ** 32) >>> 0)
        this.unwatchScreen = watchOnScreen(this.$refs.root, visible => (this.onScreen = visible))
        this.unwatchMotion = watchReducedMotion(() => (this.reduced = reducedMotion()))
        // Let the page paint before spending ~100 ms on the render.
        requestAnimationFrame(() => {
            const { width, height } = this.size
            this.ctx = this.$refs.canvas && this.$refs.canvas.getContext('2d')
            if (!this.ctx) return
            const scratch = document.createElement('canvas')
            scratch.width = width
            scratch.height = height
            this.work = scratch.getContext('2d', { willReadFrequently: true })
            this.work.imageSmoothingEnabled = false

            this.logo = renderLogo(2)
            this.shadow = toCanvas(this.logo.shadow)
            this.letters = this.logo.layers.map(toLayer)
            this.variants = this.letters.map(() => [])
            this.calm = cloneBitmap(this.logo.image)
            // The permanent damage from the print, same seed every time.
            glitchLogo(this.calm, this.logo, new XorShift128(2027), 0.8)
            this.show(this.calm)
            this.ready = true
            this.scheduleIdle()
            this.prepareVariants()
        })
    },
    beforeUnmount () {
        this.unwatchScreen()
        this.unwatchMotion()
        clearTimeout(this.frameTimer)
        clearTimeout(this.idleTimer)
        this.logo = null
    },
    methods: {
        show (b) {
            if (this.ctx) this.ctx.putImageData(new ImageData(b.d, b.w, b.h), 0, 0)
        },
        // A letter cut into bands that each slide a little.
        drawSliced (g, layer, x, y) {
            const { canvas: c } = layer
            const rng = this.rng
            for (let sy = 0; sy < c.height;) {
                const h = Math.min(c.height - sy, (3 + rng.int(18)) * 2)
                const shift = rng.float() < 0.5 ? 0 : (rng.float() < 0.5 ? -1 : 1) * (2 + rng.int(12)) * 2
                g.drawImage(c, 0, sy, c.width, h, x + shift, y + sy, c.width, h)
                sy += h
            }
        },
        // Builds one glitched frame. `only` limits the damage to one letter
        // and skips the overlays, for the small twitches between bursts.
        compose (strength, only) {
            const g = this.work
            const rng = this.rng
            const logo = this.logo
            const { width, height } = this.size
            g.clearRect(0, 0, width, height)
            g.drawImage(this.shadow, 0, 0)

            // Big bursts sometimes put the letters in the wrong order.
            const slots = [0, 1, 2]
            if (only === undefined && strength >= 3 && rng.float() < 0.35) {
                for (let i = slots.length - 1; i > 0; i--) {
                    const j = rng.int(i + 1)
                    const t = slots[i]
                    slots[i] = slots[j]
                    slots[j] = t
                }
            }

            this.letters.forEach((base, i) => {
                const hit = only === undefined ? rng.float() < 0.3 * strength : i === only
                const pool = this.variants[i]
                const layer = hit && pool.length && rng.float() < 0.75 ? rng.pick(pool) : base
                let x = layer.x + logo.letters[slots[i]].x - logo.letters[i].x
                let y = layer.y
                if (hit && rng.float() < 0.4) {
                    x += (rng.float() < 0.5 ? -1 : 1) * (1 + rng.int(6)) * 2
                    y += (rng.int(5) - 2) * 2
                }
                const effect = hit ? rng.float() : 1
                if (effect < 0.25) {
                    this.drawSliced(g, layer, x, y)
                } else if (effect < 0.4) {
                    // Squashed or stretched from the top edge.
                    const c = layer.canvas
                    const fx = 0.85 + rng.float() * 0.3
                    const fy = 0.7 + rng.float() * 0.55
                    g.drawImage(c, x, y, c.width * fx, c.height * fy)
                } else {
                    g.drawImage(layer.canvas, x, y)
                }
            })

            const frame = g.getImageData(0, 0, width, height)
            const bitmap = { w: width, h: height, d: frame.data }
            if (only === undefined) glitchLogo(bitmap, logo, rng, strength * 0.7)
            return bitmap
        },
        scheduleIdle () {
            clearTimeout(this.idleTimer)
            if (!this.animating || !this.calm) return
            const rng = this.rng
            const wait = this.hovering ? 250 + rng.float() * 450 : 900 + rng.float() * 1900
            this.idleTimer = setTimeout(() => {
                if (this.hovering || rng.float() < 0.35) this.burst()
                else this.twitch()
            }, wait)
        },
        // One letter misbehaves for a single frame.
        twitch () {
            if (!this.animating || !this.calm) return this.scheduleIdle()
            this.show(this.compose(1, this.rng.int(3)))
            this.frameTimer = setTimeout(() => {
                this.show(this.calm)
                this.scheduleIdle()
            }, 60 + this.rng.float() * 60)
        },
        burst (duration = 200 + this.rng.float() * 300) {
            if (!this.animating || !this.calm) return
            clearTimeout(this.frameTimer)
            const end = performance.now() + duration
            const strength = duration > 600 ? 3 : this.hovering ? 2.4 : 1.8
            const step = () => {
                if (performance.now() >= end) {
                    this.show(this.calm)
                    this.scheduleIdle()
                    return
                }
                this.show(this.compose(strength))
                this.frameTimer = setTimeout(step, 45 + this.rng.float() * 50)
            }
            step()
        },
        // Renders the damaged letters one at a time, in the browser's spare time.
        prepareVariants () {
            const next = () => {
                if (!this.logo || this.reduced) return
                const index = this.variants.findIndex(pool => pool.length < VARIANTS)
                if (index < 0) return
                this.variants[index].push(toLayer(renderGlitchedLetter(this.logo, index, this.rng)))
                whenIdle(next, 1000)
            }
            whenIdle(next, 1000)
        },
    },
}
</script>

<style scoped>
.ssm-logo {
  display: inline-block;
  line-height: 0;
  max-width: 100%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

canvas {
  width: 664px;
  max-width: 100%;
  height: auto;
  image-rendering: pixelated;
  opacity: 0;
}

.is-ready canvas {
  opacity: 1;
  animation: ssm-boot 0.45s steps(6, end);
}

@media (prefers-reduced-motion: reduce) {
  .is-ready canvas {
    animation: none;
  }
}
</style>
